import { useEffect } from "react";
import { Box, Group } from "@mantine/core";
import {
  DataTable, EmptyRow, GhostButton, Prompt, Screen, type Column,
} from "../components/primitives";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import type { Member } from "../types/domain";
import css from "./screens.module.css";

const COLUMNS: Column[] = [
  { key: "member", label: "member", width: "1.6fr" },
  { key: "email", label: "email", width: "1.8fr" },
  { key: "role", label: "role", width: "0.9fr" },
  { key: "last", label: "last active", width: "0.9fr" },
];

export default function WorkspacePage() {
  const openModal = useUiStore((s) => s.openModal);
  const openDetail = useUiStore((s) => s.openDetail);
  const currentId = useWorkspaceStore((s) => s.currentId);
  const members = useWorkspaceStore((s) => s.members);
  const loadMembers = useWorkspaceStore((s) => s.loadMembers);

  useEffect(() => {
    if (currentId) void loadMembers();
  }, [currentId, loadMembers]);

  return (
    <Screen>
      <Prompt
        command="vf workspace members"
        action={<GhostButton onClick={() => openModal("invite")}>+ invite member</GhostButton>}
      />

      <Box className={css.tiles3} mb={20}>
        <Box className={css.statTile}>
          <div className={css.statKey}>workspace id</div>
          <div style={{ color: "var(--vf-acc)", fontSize: 15 }}>{currentId ?? "—"}</div>
        </Box>
        <Box className={css.statTile}>
          <div className={css.statKey}>members</div>
          <div className={`${css.statValue} ${css.statValueSm}`}>{members.length}</div>
        </Box>
        <Box className={css.statTile}>
          <div className={css.statKey}>grant pattern</div>
          <div style={{ color: "var(--vf-ok)", fontSize: 14, paddingTop: 3 }}>
            {currentId ? `${currentId}/*` : "—"}
          </div>
        </Box>
      </Box>

      <DataTable<Member>
        fontSize="12.5px"
        columns={COLUMNS}
        rows={members}
        rowKey={(m) => m.uid}
        onRowClick={(m) => openDetail("member", m.uid)}
        empty={
          <EmptyRow
            title="no members yet"
            body="Invite a teammate and they get the workspace grant pattern on first sign-in."
          />
        }
        renderCell={(m, key) => {
          switch (key) {
            case "member":
              return (
                <Group gap={9} wrap="nowrap">
                  <span className={css.avatarSm}>{m.initials}</span>
                  <span style={{ color: "var(--vf-text)" }}>{m.name}</span>
                </Group>
              );
            case "email":
              return <span style={{ color: "var(--vf-sec)" }}>{m.email}</span>;
            case "role":
              return <span style={{ color: "var(--vf-ok)" }}>{m.role}</span>;
            case "last":
              return <span style={{ color: "var(--vf-sec)" }}>{m.lastActive}</span>;
            default:
              return null;
          }
        }}
      />
    </Screen>
  );
}
